import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Mail, Shield, Search, Info, Settings, Users, Activity, CheckCircle, RefreshCw, AlertTriangle, Terminal, FileText, LockOpen, Server, Database, ArrowRightLeft } from 'lucide-react';
import { MailTroubleshooterWidget } from '@/components/MailTroubleshooterWidget';

export default function MailArchitecturePage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const { authAxios } = useAuth();
  
  // States für Button-Simulation
  const [actionStatus, setActionStatus] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [terminalOutput, setTerminalOutput] = useState('');
  
  const [unbanIp, setUnbanIp] = useState('');

  const executeAction = async (actionName, backendAction, targetIp = null) => {
    setActionStatus('loading');
    setActionMessage(`${actionName} wird ausgeführt...`);
    setTerminalOutput('');
    
    try {
      const api = authAxios();
      const payload = { action: backendAction };
      if (targetIp) payload.target_ip = targetIp;
      
      const response = await api.post('/system/action', payload);
      
      setActionStatus('success');
      setActionMessage(`${actionName} erfolgreich.`);
      setTerminalOutput(response.data.output || 'Keine Ausgabe.');
      
    } catch (error) {
      console.error(error);
      setActionStatus('error');
      setActionMessage(`Fehler bei ${actionName}.`);
      setTerminalOutput(error.response?.data?.detail || error.message || 'Ein unbekannter Fehler ist aufgetreten.');
    } finally {
      setTimeout(() => {
        setActionStatus(null);
      }, 5000);
    }
  };

  const matches = (text) => {
    if (!searchQuery) return true;
    return text.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {matches('Architektur Proxy Netcup Blacklisting Fail2Ban') && (
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4">E-Mail Architektur (Layer-7 Proxy)</h3>
              <p className="text-[#a1a1aa] mb-4 leading-relaxed">
                Unsere E-Mail-Architektur verhindert das "Blacklisting" durch Netcup. Kunden verbinden sich mit <code>mail.fyremail.de</code> (152.53.204.79). Die Postfächer liegen bei Netcup (<code>mxe953.netcup.net</code>).
              </p>
              
              <h4 className="text-white font-bold mb-2 mt-6">Das Problem & Die Lösung</h4>
              <p className="text-[#a1a1aa] mb-4 leading-relaxed">
                Nginx agiert als Layer-7 Mail-Proxy, terminiert SSL und prüft Passwörter lokal. Gibt ein Kunde das Passwort 5x falsch ein, sperrt <strong>unser</strong> Fail2Ban die IP, bevor Netcup blockieren kann.
              </p>

              <h4 className="text-white font-bold mb-2 mt-6">Proxy Architektur (Interaktiver Datenfluss)</h4>
              <p className="text-[#a1a1aa] mb-4 leading-relaxed text-sm">
                Visualisierung des Hybrid-Setups: IMAP über <code>nginx mail</code> (993 → 143) und SMTP über <code>nginx stream</code> (465/587 → 465), mit lokalem Auth und Fail2Ban.
              </p>

              <div className="p-6 bg-black/40 rounded-lg border border-white/5 overflow-hidden relative">
                <div className="flex flex-col items-center justify-center gap-6 py-4">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center z-10 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                      <Mail className="w-6 h-6 text-blue-400" />
                    </div>
                    <span className="text-xs font-bold text-white mt-2">Mail Client (Kunde)</span>
                    <span className="text-[10px] text-[#a1a1aa]">IMAP 993 · SMTP 465/587</span>
                  </div>

                  <div className="h-10 w-px relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/50 to-green-500/50"></div>
                    <div className="absolute top-0 left-1/2 w-2 h-2 -translate-x-1/2 bg-blue-400 rounded-full animate-[ping_1s_ease-in-out_infinite]"></div>
                  </div>

                  <div className="w-full max-w-md bg-green-500/5 border border-green-500/20 rounded-xl p-4 flex flex-col items-center relative z-10">
                    <div className="absolute -top-3 px-3 py-0.5 bg-black border border-green-500/30 rounded-full text-[10px] text-green-400 font-bold uppercase tracking-wider">
                      Proxy (152.53.204.79)
                    </div>
                    <Server className="w-8 h-8 text-green-400 mb-2" />
                    <span className="font-bold text-white">Nginx Mail Proxy</span>
                    <span className="text-xs text-green-400/70">mail.fyremail.de</span>

                    <div className="w-full flex justify-between mt-4 border-t border-white/5 pt-4">
                      <div className="flex flex-col items-center">
                        <Shield className="w-4 h-4 text-red-400 mb-1" />
                        <span className="text-[10px] text-[#a1a1aa]">Fail2Ban</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <Info className="w-4 h-4 text-yellow-400 mb-1" />
                        <span className="text-[10px] text-[#a1a1aa]">Auth (9080)</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <ArrowRightLeft className="w-4 h-4 text-blue-400 mb-1" />
                        <span className="text-[10px] text-[#a1a1aa]">SSL Term.</span>
                      </div>
                    </div>
                  </div>

                  <div className="h-12 w-px relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-green-500/50 to-purple-500/50"></div>
                  </div>

                  <div className="w-full max-w-md bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 flex flex-col items-center relative">
                    <div className="absolute -top-3 px-3 py-0.5 bg-black border border-purple-500/30 rounded-full text-[10px] text-purple-300 font-bold uppercase tracking-wider">
                      Backend
                    </div>
                    <Database className="w-8 h-8 text-purple-300 mb-2" />
                    <span className="font-bold text-white">Netcup Mailserver</span>
                    <span className="text-xs text-purple-300/70">mxe953.netcup.net</span>
                    <div className="w-full grid grid-cols-2 gap-2 mt-4 border-t border-white/5 pt-4">
                      <div className="text-[10px] text-[#a1a1aa] bg-black/30 border border-white/10 rounded-md px-2 py-1 text-center">
                        IMAP: 993 → 143
                      </div>
                      <div className="text-[10px] text-[#a1a1aa] bg-black/30 border border-white/10 rounded-md px-2 py-1 text-center">
                        SMTP: 465/587 → 465
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        );
      case 'docs':
        return (
          <div className="space-y-4">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">Technische Details & Logs</h3>
              
              <div className="space-y-6">
                {matches('Zertifikate SSL SNI HestiaCP') && (
                <div>
                  <h4 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#EF694C]" /> SSL-Zertifikate (SNI)
                  </h4>
                  <p className="text-[#a1a1aa] pl-7">
                    Zertifikate für <code>mail.fyremail.de</code> werden über HestiaCP generiert.
                  </p>
                </div>
                )}

                {matches('Fail2Ban Limits Jail Sperre') && (
                <div>
                  <h4 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#EF694C]" /> Fail2Ban Jail "nginx-mail-auth"
                  </h4>
                  <p className="text-[#a1a1aa] pl-7">
                    <strong>Limits:</strong> 5 Versuche in 10 Minuten. Bann-Zeit: 1 Stunde.
                  </p>
                </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'config':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
               <h3 className="text-xl font-bold text-white mb-6">Steuerung & Tools</h3>

               <div className="mb-8">
                 <MailTroubleshooterWidget />
               </div>

               <h3 className="text-lg font-bold text-white mb-4">Manuelle Werkzeuge</h3>
               <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-8">
                 {/* Nginx Card */}
                 {matches('Nginx Config Neustart /etc/nginx/mail_proxy.conf') && (
                 <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                   <div>
                     <h4 className="font-bold text-white mb-2">Nginx Mail-Proxy</h4>
                     <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">/etc/nginx/mail_proxy.conf</code>
                     <p className="text-sm text-[#a1a1aa] mb-4">Verwaltet die Ports 993, 465, 587 und SSL-Zertifikate.</p>
                   </div>
                   <div className="flex gap-2">
                     <button 
                       onClick={() => executeAction('Lese Nginx Mail Config', 'read_nginx_mail_config')}
                       className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2 rounded text-sm transition-colors"
                     >
                       <FileText className="w-4 h-4" /> Config
                     </button>
                     <button 
                       onClick={() => executeAction('Nginx Neustart', 'restart_nginx_service')}
                       className="flex-1 flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 rounded text-sm transition-colors"
                     >
                       <RefreshCw className="w-4 h-4" /> Neustart
                     </button>
                   </div>
                 </div>
                 )}

                 {/* Logs Card */}
                 {matches('Logs Fehler /var/log/nginx/error.log tail') && (
                 <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                   <div>
                     <h4 className="font-bold text-white mb-2">Nginx Error Logs</h4>
                     <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">tail -f /var/log/nginx/error.log</code>
                     <p className="text-sm text-[#a1a1aa] mb-4">Hier landen fehlerhafte Logins (die Fail2Ban ausliest).</p>
                   </div>
                   <button 
                     onClick={() => executeAction('Live Logs (Tail)', 'tail_nginx_error')}
                     className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2 rounded text-sm transition-colors w-full"
                   >
                     <Terminal className="w-4 h-4" /> Letzte Fehler-Logs anzeigen
                   </button>
                 </div>
                 )}

                 {/* Fail2Ban Card */}
                 {matches('Fail2Ban Status IP entsperren unbanip') && (
                 <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between col-span-1 xl:col-span-2">
                   <div>
                     <h4 className="font-bold text-white mb-2">Fail2Ban Verwaltung (nginx-mail-auth)</h4>
                     <p className="text-sm text-[#a1a1aa] mb-4">Überprüfen Sie blockierte Kunden-IPs und entsperren Sie diese manuell.</p>
                   </div>
                   
                   <div className="flex flex-col md:flex-row gap-4 items-end">
                       <button 
                         onClick={() => executeAction('Fail2Ban Status Check', 'fail2ban_mail_status')}
                         className="flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 px-4 rounded text-sm transition-colors whitespace-nowrap h-[42px]"
                       >
                       <Shield className="w-4 h-4" /> Aktuelle Sperren auflisten
                     </button>
                     
                     <div className="flex-1 flex gap-2 w-full">
                       <input 
                         type="text" 
                         value={unbanIp}
                         onChange={(e) => setUnbanIp(e.target.value)}
                         placeholder="IP-Adresse (z.B. 84.150.12.34)" 
                         className="flex-1 bg-black/50 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#EF694C] h-[42px]"
                       />
                       <button 
                         onClick={() => {
                           if(!unbanIp) return;
                           executeAction(`IP ${unbanIp} entsperren`, 'unban_ip', unbanIp);
                           setUnbanIp('');
                         }}
                         className="flex items-center justify-center gap-2 bg-green-500/10 hover:bg-green-500/20 text-green-500 border border-green-500/20 py-2 px-4 rounded text-sm transition-colors whitespace-nowrap h-[42px]"
                       >
                         <LockOpen className="w-4 h-4" /> Entsperren
                       </button>
                     </div>
                   </div>
                 </div>
                 )}
               </div>

               {/* Global Terminal / Status */}
               {actionStatus && (
                 <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                   actionStatus === 'loading' ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400' :
                   actionStatus === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
                   'bg-red-500/10 border border-red-500/30 text-red-400'
                 }`}>
                   {actionStatus === 'loading' && <RefreshCw className="w-5 h-5 animate-spin" />}
                   {actionStatus === 'success' && <CheckCircle className="w-5 h-5" />}
                   {actionStatus === 'error' && <AlertTriangle className="w-5 h-5" />}
                   <span className="text-sm font-medium">{actionMessage}</span>
                 </div>
               )}

               {terminalOutput && (
                 <div className="bg-black p-4 rounded-lg border border-white/10 font-mono text-sm text-green-400 whitespace-pre-wrap max-h-64 overflow-y-auto mt-4">
                   {terminalOutput}
                 </div>
               )}

            </div>
          </div>
        );
      case 'contacts':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
               <h3 className="text-xl font-bold text-white mb-6">Eskalationsmatrix (E-Mail / Blacklisting)</h3>
               
               <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/10 before:to-transparent">
                 
                 {matches('Level 1 Kunden-Fehlkonfiguration') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-green-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(34,197,94,0.4)] z-10">
                     1
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-green-500/30 bg-green-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">Level 1: Kunden-Fehlkonfiguration</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Symptom:</strong> Passwort wird nicht akzeptiert oder Zertifikatswarnungen.</p>
                     <p className="text-sm text-green-400"><strong>Aktion:</strong> Snappymail Login prüfen. Server <code>mail.fyremail.de</code> prüfen.</p>
                   </div>
                 </div>
                 )}

                 {matches('Level 2 Lokale IP-Sperre Fail2Ban') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-yellow-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(234,179,8,0.4)] z-10">
                     2
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">Level 2: Lokale IP-Sperre (Fail2Ban)</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Symptom:</strong> Kunde hat gar keine Verbindung (Timeouts).</p>
                     <p className="text-sm text-yellow-400"><strong>Aktion:</strong> Fail2Ban IP-Sperre über den "Steuerung"-Tab prüfen und entsperren.</p>
                   </div>
                 </div>
                 )}

                 {matches('Level 3 Netcup Blacklisting Totalausfall') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-red-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(239,68,68,0.4)] z-10">
                     3
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-red-500/30 bg-red-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">Level 3: Netcup Blacklisting</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Symptom:</strong> Kein Kunde kann mehr E-Mails abrufen.</p>
                     <p className="text-sm text-red-400"><strong>Aktion:</strong> Support-Ticket bei Netcup eröffnen.</p>
                   </div>
                 </div>
                 )}
               </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-white tracking-tight">E-Mail Architektur</h1>
          <p className="text-[#a1a1aa] mt-2">Detaillierte Übersicht unseres Layer-7 Mail-Proxys und Routing-Strategien (v2.1)</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a1a1aa]" />
          <input 
            type="text"
            placeholder="Suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 bg-[#121214] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#EF694C] w-64 transition-colors"
          />
        </div>
      </div>

      <div className="flex overflow-x-auto border-b border-white/10 no-scrollbar">
        {[
          { id: 'overview', label: 'Proxy Architektur', icon: Mail },
          { id: 'docs', label: 'Dokumentation', icon: Info },
          { id: 'config', label: 'Steuerung & Tools', icon: Settings },
          { id: 'contacts', label: 'Eskalationspfade', icon: Users }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-[#EF694C] text-[#EF694C]' 
                  : 'border-transparent text-[#a1a1aa] hover:text-white hover:border-white/20'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-[500px]">
        {renderContent()}
      </div>
    </div>
  );
}
